import React, { useMemo, createContext } from 'react';

import { BuilderBlocks } from '@builder.io/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import TabComponentWrapper, {
  StyledTab,
  StyledTabPanel,
} from '../Tabs/TabComponentWrapper';

export const TabsContext = createContext();

const TabsWrapper = styled.div`
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.xlarge - 1}px) {
    padding-top: ${({ theme }) => theme.spacing.small}px;
  }

  .slick-arrow {
    background: ${({ theme }) => theme.colors.white};
    width: 42px;

    &.slick-prev:before {
      background: ${({ theme }) => theme.colors.white};
      content: ' ';
      height: 20px;
      left: -30px;
      position: absolute;
      width: 60px;
      z-index: -1;
    }
  }
`;

// workaround so that the styles applied to the Tabs component in Builder
// aren't inherited in the BuilderBlocks, resulting in unexpected behavior.
// using font/all here would override unwanted properties
const StyledBuilderBlocks = styled(BuilderBlocks)`
  font-size: initial;
  font-style: initial;
  font-weight: initial;
  line-height: initial;
  letter-spacing: initial;
  text-align: initial;
  color: initial;
`;

const StyledTabTitle = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.tiny}px;
`;

const BuilderTabs = ({
  tabs: inputTabs = [],
  showArrowsOnMobile = false,
  builderBlock,
}) => {
  const tabsOptions = useMemo(() => {
    if (!inputTabs.length) {
      return null;
    }

    return (inputTabs || []).reduce(
      (acc, tab, index) => {
        acc.tabs.push(
          <StyledTab key={index}>
            <StyledTabTitle>{tab.label}</StyledTabTitle>
          </StyledTab>
        );

        acc.tabPanels.push(
          <StyledTabPanel key={index}>
            <StyledBuilderBlocks
              parentElementId={builderBlock.id}
              dataPath={`component.options.tabs.${index}.content`}
              blocks={inputTabs[index].content}
            />
          </StyledTabPanel>
        );

        return acc;
      },
      {
        tabs: [],
        tabPanels: [],
      }
    );
  }, [inputTabs, builderBlock.id]);

  if (!tabsOptions) {
    return null;
  }

  const { tabs, tabPanels } = tabsOptions;

  return (
    <TabsContext.Provider
      value={{
        showArrowsOnMobile,
      }}
    >
      <TabsWrapper>
        <TabComponentWrapper tabs={tabs} tabPanels={tabPanels} />
      </TabsWrapper>
    </TabsContext.Provider>
  );
};

BuilderTabs.propTypes = {
  builderBlock: PropTypes.any,
  showArrowsOnMobile: PropTypes.bool,
  tabs: PropTypes.array,
};

export default BuilderTabs;
